import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { useLocation, useNavigate } from 'react-router-dom';
import SubscriptionCard from '../../UIControls/SubscriptionCard';
import subscriptionPlans from './subscriptionPlans';
import { CenteredWrapper } from '../../UIControls/centered-wrapper';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import ChangePlanModal from '../../UIControls/ChangePlanModal';
import SettingsCancelPlan from './settings-cancel-plan';
import CancelPlanModal from '../../UIControls/CancelPlanModal';
import SettingsFAQ from './settings-faq';
import { useSnackbar } from '../SnackbarProvider';
import { toJS } from 'mobx';
import PaymentApi from '../../../rest-api/payment-api';
import UserApi from '../../../rest-api/user-api';
import { useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import KlaviyoApi from '../../../rest-api/klaviyo-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import config from '../../../config';
import getDiscountByPlan from '../../helper/getDiscountHelper';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import mixpanel from 'mixpanel-browser';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    borderRadius: 50,
    overflow: 'hidden',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)',
    width: 205,
}));

const StyledToggleButton = styled(ToggleButton)(({ theme, selected }) => ({
    color: selected ? '#fff' : '#000',
    backgroundColor: selected ? '#000453' : '#fff',
    textTransform: 'none',
    '&:hover': {
        backgroundColor: selected ? '#000453' : '#f4f4f4',
    },
    '&.Mui-selected': {
        backgroundColor: '#000453',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#000453',
        },
    },
    border: 'none',
    borderRadius: 0,
    flex: 1,
}));

const ManageSubscription = () => {
    const stripe = useStripe();
    const elements = useElements();
    const planKeys = Object.keys(subscriptionPlans);
    const appStateStore = useContext(StoreContext);
    const { authStore, userDataStore } = useContext(StoreContext);
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
    const [subscriptionExpiryTime, setSubscriptionExpiryTime] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [paymentDiscount, setPaymentDiscount] = useState(0); // that was not in use; Changed to 0% CETRA-371
    const [paymentDetailsBillingCycle, setPaymentDetailsBillingCycle] = useState(50);

    const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
    const [cancelPlanModalOpen, setCancelPlanModalOpen] = useState(false);
    const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
    const [downgradeConfirmOpen, setDowngradeConfirmOpen] = useState(false);

    // track -  we are waiting on any payment/subscription API calls
    const [isProcessing, setIsProcessing] = useState(false);
    const showSnackbar = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const stripeCustomerId = userDataStore?.subscriptionData?.stripeCustomerId;
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showAnnualDiscountText = billingCycle === 'annually';
    const [discounts, setDiscounts] = useState({});
    const pendingPlan = userDataStore?.subscriptionData?.pendingPlan;
    const pendingPlanEndDate = userDataStore?.subscriptionData?.subscriptionEndDate;
    const pendingPlanEndDateLabel = pendingPlanEndDate
        ? new Date(pendingPlanEndDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : null;
    const downgradeEffectiveDateLabel = userDataStore?.subscriptionData?.subscriptionEndDate
        ? new Date(userDataStore.subscriptionData.subscriptionEndDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : null;

    const fetchPaymentMethod = async () => {
        try {
            if (stripeCustomerId) {
                const paymentDetails = await PaymentApi.getPaymentDetails(authStore.idToken, stripeCustomerId);
                console.warn(paymentDetails);
                setPaymentDetails(paymentDetails);
                if (paymentDetails?.subscription?.discount?.coupon?.percent_off) {
                    console.warn('coupon found ', paymentDetails.subscription.discount.coupon);
                    setPaymentDiscount(paymentDetails.subscription.discount.coupon.percent_off);
                }
                if (paymentDetails?.subscription?.plan) {
                    setPaymentDetailsBillingCycle(paymentDetails?.subscription?.plan.interval);
                }
            }
        } catch (error) {
            console.error('Error fetching payment method:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchDiscounts = async () => {
            const newDiscounts = {};

            for (const plan of planKeys) {
                const discount = await getDiscountByPlan(authStore.idToken, plan);
                // getDiscountByPlan returns a percentage number, so wrap it in our new format
                newDiscounts[plan] = discount ? { type: 'percent', value: discount } : null;
            }

            console.warn('Fetched Discounts: ', newDiscounts);
            setDiscounts(newDiscounts);
        };

        fetchDiscounts();
    }, [authStore.idToken]);

    useEffect(() => {
        fetchPaymentMethod();
    }, [userDataStore, authStore]);

    useEffect(() => {
        window.scrollTo(0, 0);
        console.warn(toJS(userDataStore));
        if (location.state && location.state.subscriptionUpdated) {
            showSnackbar(
                'Your subscription details have been successfully updated. Start enjoying the benefits now!',
                'success',
            );
            // clear state to prevent the message from showing again on subsequent navigations
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, showSnackbar, navigate]);

    useEffect(() => {
        const subscriptionEndDate = userDataStore?.subscriptionData?.subscriptionEndDate;
        if (subscriptionEndDate) {
            const currentTime = Date.now();
            const endTimeInMilliseconds = new Date(subscriptionEndDate).getTime();
            const differenceInMilliseconds = endTimeInMilliseconds - currentTime;
            const millisecondsInOneDay = 24 * 60 * 60 * 1000;
            const differenceInDays = differenceInMilliseconds / millisecondsInOneDay;
            setSubscriptionExpiryTime(Math.max(0, Math.floor(differenceInDays)));
        } else {
            setSubscriptionExpiryTime(null);
        }
    }, [userDataStore?.subscriptionData?.subscriptionEndDate]);

    const handlePostSubscriptionRedirect = (previousPlan, newPlan, userDataStore, navigate, showSnackbar, response) => {
        let shouldRedirectToSettings = false;

        if (previousPlan === 'Student' && (newPlan === 'Professional' || newPlan === 'Clinic')) {
            shouldRedirectToSettings = true;
        } else if (previousPlan === 'Professional' && newPlan === 'Clinic') {
            const clinicName = userDataStore.userData.clinicName;
            if (!clinicName || clinicName.trim() === '') {
                shouldRedirectToSettings = true;
            }
        }

        if (shouldRedirectToSettings) {
            navigate('/settings', { state: { scrollToPracticeDetails: true } });
        } else if (response.subscription) {
            navigate('/', { state: { subscriptionSuccess: true } });
        } else {
            showSnackbar('Billing details have been successfully updated.', 'success');
        }
    };

    const trackFreeUpgrade = (previousPlan, newPlan, userId, email) => {
        if (previousPlan === 'Free' && newPlan === 'Professional') {
            mixpanel.track('UpgradeFromFreeToProfessional', { userId, email });
        } else if (previousPlan === 'Free' && newPlan === 'Clinic') {
            mixpanel.track('UpgradeFromFreeToClinic', { userId, email });
        }
    };

    const trackDowngradeToFree = (previousPlan, newPlan, userId, email) => {
        if (newPlan !== 'Free') return;
        if (previousPlan === 'Professional') {
            mixpanel.track('DowngradeFromProfessionalToFree', { userId, email });
        } else if (previousPlan === 'Clinic') {
            mixpanel.track('DowngradeFromClinicToFree', { userId, email });
        }
    };

    const handleBillingCycleChange = (event, newCycle) => {
        if (newCycle !== null) {
            setBillingCycle(newCycle);
        }
    };

    const executeFreeDowngrade = async () => {
        try {
            setIsProcessing(true);
            console.log('Free plan selected', {
                userId: userDataStore.userId,
                stripeCustomerId,
                hasSubscription: !!paymentDetails?.subscription,
                currentPlan: userDataStore.subscriptionData?.accountType,
            });

            const freePlan = subscriptionPlans.Free;
            const freePlanDetails = {
                priceId: freePlan.priceMonthlyId,
                productId: freePlan.productId,
                isMonthly: true,
                accountType: freePlan.accountType,
            };

            // If already Free, no further action needed
            if (userDataStore.subscriptionData?.accountType === 'Free') {
                showSnackbar('You are already on the Free plan', 'success');
                return;
            }

            if (!stripeCustomerId || !paymentDetails?.subscription) {
                // No active subscription found - provision Free subscription in Stripe
                const paymentResponse = await PaymentApi.updatePaymentDetails(authStore.idToken, {
                    userId: userDataStore.userId,
                    email: userDataStore.userData.emailAddress,
                    plan: freePlanDetails,
                });

                const subscriptionData = {
                    accountType: 'Free',
                    trialActive: false,
                    trialEndDate: null,
                    subscriptionEndDate: paymentResponse?.subscription?.current_period_end
                        ? paymentResponse.subscription.current_period_end * 1000
                        : null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: paymentResponse?.customerId || stripeCustomerId || null,
                };

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData,
                });

                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);

                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, userDataStore.userData.emailAddress, 'Free');

                showSnackbar('Free plan activated', 'success');
                window.location.reload();
                return;
            }

            // Cancel existing subscription at period end and schedule downgrade
            const cancelResponse = await PaymentApi.cancelSubscription(authStore.idToken, {
                userId: userDataStore.userId,
            });
            console.log('Cancel subscription response', cancelResponse);

            const currentPeriodEnd = cancelResponse?.subscription?.current_period_end
                ? cancelResponse.subscription.current_period_end * 1000
                : userDataStore?.subscriptionData?.subscriptionEndDate || null;

            const subscriptionData = {
                ...userDataStore.subscriptionData,
                cancelled: true,
                subscriptionEndDate: currentPeriodEnd,
                pendingPlan: {
                    accountType: 'Free',
                    productId: freePlanDetails.productId,
                    priceId: freePlanDetails.priceId,
                    isMonthly: freePlanDetails.isMonthly,
                },
            };

            await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

            userDataStore.setUserData({
                ...userDataStore.getUserData(),
                subscriptionData,
            });

            authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);

            trackDowngradeToFree(
                userDataStore.subscriptionData?.accountType,
                'Free',
                userDataStore.userId,
                userDataStore.userData.emailAddress,
            );

            const resp = await KlaviyoApi.updateUserInKlaviyo(
                authStore.idToken,
                userDataStore.userData.emailAddress,
                userDataStore.subscriptionData?.accountType,
            );
            console.log('Klaviyo downgrade response:', resp);

            showSnackbar('Downgrade scheduled for the end of your billing period', 'success');
            window.location.reload();
        } catch (err) {
            console.error('Error downgrading to Free plan:', err);
            showSnackbar('Error when downgrading to Free plan', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCardClick = async (plan) => {
        if (isMultiUser) return;
        if (!authStore?.idToken) {
            await authStore.refreshToken();
        }
        if (!authStore?.idToken) {
            showSnackbar('Authentication is still loading. Please try again in a moment.', 'error');
            return;
        }

        const subscriptionEndDate = userDataStore?.subscriptionData?.subscriptionEndDate;
        const currentTime = Date.now();
        console.warn(userDataStore.subscriptionData?.accountType, plan, subscriptionEndDate, currentTime);

        // If user is already on that plan, do nothing:
        if (
            userDataStore.subscriptionData?.accountType === plan &&
            Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate
        ) {
            return false;
        }

        // If plan is Enterprise, open a *new* Enterprise-only modal (no Stripe flows)
        if (plan === 'Enterprise') {
            setEnterpriseModalOpen(true);
            return; // Stop any further plan-change logic
        }

        // If plan is Free, downgrade immediately (no payment needed)
        if (plan === 'Free') {
            // If already Free, no confirmation needed
            if (userDataStore.subscriptionData?.accountType === 'Free') {
                showSnackbar('You are already on the Free plan', 'success');
                return;
            }
            setDowngradeConfirmOpen(true);
            return;
        }

        console.warn('paymentDetails should have both keys: ', paymentDetails);

        const hasActiveSubscription = paymentDetails?.subscription;
        const hasPaymentMethods = paymentDetails?.paymentMethods && paymentDetails.paymentMethods.length > 0;
        console.warn(
            hasActiveSubscription,
            hasPaymentMethods,
            stripeCustomerId,
            userDataStore.subscriptionData,
            subscriptionEndDate,
            currentTime,
        );
        if (
            !stripeCustomerId || // customer is yet to be added to stripe
            !userDataStore.subscriptionData || // No subscription data means no paid plan
            !subscriptionEndDate || // Subscription end date is undefined (not set)
            currentTime >= subscriptionEndDate || // Subscription has expired
            !hasActiveSubscription || // No active subscription
            !hasPaymentMethods // No payment methods saved
        ) {
            navigate('/checkout', { state: { plan, billingCycle } });
        } else {
            setSelectedPlan(plan);
            setChangePlanModalOpen(true);
        }
    };

    const handleConfirmChangePlan = async () => {
        setChangePlanModalOpen(false);
        // Start spinner
        setIsProcessing(true);
        try {
            console.warn('current plan', userDataStore.subscriptionData?.accountType);

            console.warn('new plan', selectedPlan);

            const planDetails = subscriptionPlans[selectedPlan];

            planDetails.isMonthly = billingCycle === 'monthly';
            planDetails.priceId = billingCycle === 'monthly' ? planDetails.priceMonthlyId : planDetails.priceAnnuallyId;

            console.warn('new plan details ', planDetails);

            console.warn('selected billing cycle ', billingCycle);

            console.warn('use payment method: ', paymentDetails['paymentMethods'][0]);

            await sendUpdateSubscriptionToBackend(paymentDetails['paymentMethods'][0], planDetails);
        } catch (error) {
            console.error(error);
        } finally {
            // Stop spinner
            setIsProcessing(false);
        }
    };

    const sendUpdateSubscriptionToBackend = async (paymentMethod, plan) => {
        try {
            const req = {
                userId: userDataStore.userId,
                paymentMethodId: paymentMethod.id,
                email: userDataStore.userData.emailAddress,
                ...(plan && { plan }),
            };

            const bearerToken = authStore.idToken;

            // payment method is not needed in req but have for future just in case
            const response = await PaymentApi.updateSubscription(bearerToken, req);

            console.warn('update subscription  response: ', response);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            if (response.requiresAction) {
                showSnackbar('Payment pending - please complete the security check with your bank', 'success');
                const result = await stripe.confirmCardPayment(response.paymentIntentClientSecret);
                if (result.error) {
                    showSnackbar('3D Secure authentication failed.', 'error');
                } else {
                    // Once 3D-Secure is done
                    if (
                        result.paymentIntent.status === 'succeeded' ||
                        result.paymentIntent.status === 'requires_confirmation'
                    ) {
                        await confirmPaymentIntent(result.paymentIntent.id, response.subscriptionId);
                    }
                }
            } else if (response.subscription) {
                // Handle subscription success
                const { id, status, items, current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: plan?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null, // Convert seconds to milliseconds
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('sub data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData.accountType;
                const newPlan = plan?.accountType;

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData: updatedSubscriptionData });
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData: updatedSubscriptionData,
                });
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    plan?.accountType,
                );
                trackFreeUpgrade(previousPlan, newPlan, userDataStore.userId, userDataStore.userData.emailAddress);

                handlePostSubscriptionRedirect(previousPlan, newPlan, userDataStore, navigate, showSnackbar, response);
            }
        } catch (error) {
            console.error('Error sending payment details to backend:', error);
            showSnackbar('Error when updating billing details, please try again.', 'error');
        }
    };

    const confirmPaymentIntent = async (paymentIntentId, subscriptionId) => {
        console.warn('Check this Payment Intent: ', paymentIntentId);
        try {
            const req = {
                paymentIntentId,
                userId: userDataStore.userId,
                subscriptionId: subscriptionId,
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.confirmPaymentDetails(bearerToken, req);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            const planDetails = subscriptionPlans[selectedPlan];

            // Update customerId if returned
            if (response.customerId) {
                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    stripeCustomerId: response.customerId,
                };
            }

            if (response.subscription) {
                const { id, status, items, current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: planDetails?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('subscription data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData.accountType; // store BEFORE updating
                const newPlan = planDetails?.accountType;

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData: updatedSubscriptionData });
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData: updatedSubscriptionData,
                });
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, userDataStore.userData.emailAddress, newPlan);
                trackFreeUpgrade(previousPlan, newPlan, userDataStore.userId, userDataStore.userData.emailAddress);

                // Re-fetch payment methods to update state
                await fetchPaymentMethod();

                handlePostSubscriptionRedirect(previousPlan, newPlan, userDataStore, navigate, showSnackbar, response);
            }
        } catch (error) {
            console.error('Error confirming payment intent:', error);
            showSnackbar('Error when confirming payment.', 'error');
        }
    };

    const handleConfirmCancelPlan = async (checkedReasons) => {
        try {
            await sendCancelSubscriptionToBackend(checkedReasons);
            setCancelPlanModalOpen(false);
            window.location.reload();
        } catch (error) {
            // Not reloading the page on error
            console.error('Cancellation failed, page will not reload:', error);
        }
    };

    const sendCancelSubscriptionToBackend = async (checkedReasons) => {
        try {
            const req = {
                userId: userDataStore.userId,
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.cancelSubscription(bearerToken, req);

            if (response.error) {
                console.error('Error canceling payment:', response.error);
                // maybe sync issue between stripe and our db debug here?
                throw new Error(response.error || 'Failed to cancel subscription');
            }

            if (response.subscription) {
                const { current_period_end } = response.subscription;

                const existingSubscriptionData = userDataStore.subscriptionData;

                // they can still use subscription until end of billing peroid, only cancelled = true
                const subscriptionData = {
                    ...existingSubscriptionData,
                    cancelled: true,
                    subscriptionEndDate: current_period_end * 1000 || null, // Convert seconds to milliseconds
                    cancellationReason: checkedReasons,
                };

                await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

                // Update userDataStore with the new subscription details
                userDataStore.setUserData({
                    ...userDataStore.getUserData(),
                    subscriptionData,
                });

                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    'Cancelled',
                );
                mixpanel.track('SubscriptionCancelled', {
                    userId: userDataStore.userId,
                    email: userDataStore.userData.emailAddress,
                });
                showSnackbar('Your subscription plan has been successfully cancelled.');
            }
        } catch (error) {
            console.error('Error sending payment details to backend:', error);
            showSnackbar('Error when cancelling subscription.', 'error');
            // if no throw error here, then page will reload, hance the err not visible/readable
            throw error;
        }
    };

    const handleSignout = async () => {
        await authStore.logout();
        await appStateStore.clearUserStores();
        localStorage.clear();
    };

    const isSubscriptionDataReady = !!userDataStore?.subscriptionData?.accountType;
    const shouldShowLoading = loading || !isSubscriptionDataReady;

    return (
        <CenteredWrapper component="main" sx={{ zIndex: 1, position: 'relative', mt: '64px' }}>
            {shouldShowLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box className="settings-container" sx={{ width: '100%', maxWidth: 638 }}>
                    <Box>
                        <Typography variant="h4" sx={{ marginBottom: 1.5, fontSize: 32 }}>
                            Manage your subscription
                        </Typography>
                        <Typography variant="subtitle2" sx={{ marginBottom: 2 }}>
                            {userDataStore?.subscriptionData?.accountType &&
                                (userDataStore.subscriptionData.accountType === 'Free' ||
                                    Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate) && (
                                    <>
                                        Current plan:
                                        <strong style={{ textTransform: 'capitalize' }}>
                                            {' '}
                                            {userDataStore?.subscriptionData?.accountType}
                                        </strong>
                                        {userDataStore?.subscriptionData?.trialActive ? ' free trial' : ''}
                                    </>
                                )}
                        </Typography>
                        {pendingPlan?.accountType === 'Free' && pendingPlanEndDateLabel && (
                            <Typography variant="subtitle2" sx={{ marginBottom: 2 }}>
                                Downgrade scheduled. You will keep your current plan until {pendingPlanEndDateLabel}.
                            </Typography>
                        )}
                        <Divider sx={{ marginBottom: 4 }} />

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            {/* Show days remaining warning for paid plans (Student, Professional, Clinic, etc.)
                                Note: Free plan is excluded - it's a permanent tier, not a trial.
                                trialActive indicates a promotional trial period on a paid plan (e.g., 30-day Professional trial) */}
                            {subscriptionExpiryTime <= 30 &&
                                Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate &&
                                userDataStore?.subscriptionData?.accountType !== 'Free' && (
                                    <Typography variant="subtitle" style={{ marginBottom: '0' }}>
                                        You have <strong> {subscriptionExpiryTime} days</strong> left of your{' '}
                                        {userDataStore?.subscriptionData?.accountType}{' '}
                                        {userDataStore?.subscriptionData?.trialActive ? 'free trial' : ''} plan.
                                    </Typography>
                                )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <StyledToggleButtonGroup
                                    value={billingCycle}
                                    exclusive
                                    onChange={handleBillingCycleChange}
                                    aria-label="billing cycle"
                                    sx={{ marginTop: isMobile ? 2 : 0 }}
                                >
                                    <StyledToggleButton
                                        value="monthly"
                                        aria-label="monthly"
                                        selected={billingCycle === 'monthly'}
                                    >
                                        Monthly
                                    </StyledToggleButton>
                                    <StyledToggleButton
                                        value="annually"
                                        aria-label="annually"
                                        selected={billingCycle === 'annually'}
                                    >
                                        Annually
                                    </StyledToggleButton>
                                </StyledToggleButtonGroup>

                                {showAnnualDiscountText && (
                                    <Typography
                                        variant="body1"
                                        sx={{ fontSize: '0.875rem', textAlign: 'right', marginTop: 1 }} // Ensures it is always below the buttons
                                    >
                                        {config.annualDiscountText}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {isMultiUser && (
                            <Box
                                sx={{
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: 2,
                                    mb: 2,
                                    mt: 3,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#555' }}>
                                    ⚠️ You’re currently logged in as a clinic team member. Only the Administrator can
                                    manage or change the subscription plan.
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ textAlign: 'left', marginTop: '24px' }}>
                            {planKeys
                                .filter((plan) => !subscriptionPlans[plan].legacy)
                                .map((plan, index) => (
                                    <Box key={index} onClick={() => handleCardClick(plan)} sx={{ cursor: 'pointer' }}>
                                        <SubscriptionCard
                                            plan={plan}
                                            discount={discounts[plan]}
                                            billingCycle={billingCycle}
                                            isCurrentPlan={
                                                userDataStore?.subscriptionData?.accountType === plan &&
                                                (plan === 'Free' ||
                                                    Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate)
                                            }
                                            currentPlanBillingCycle={paymentDetailsBillingCycle}
                                            disabled={isMultiUser}
                                        />
                                        {userDataStore?.subscriptionData?.accountType === plan &&
                                            Date.now() < userDataStore?.subscriptionData?.subscriptionEndDate &&
                                            plan !== 'Free' && (
                                                <Box sx={{ fontWeight: 600 }}>
                                                    {' '}
                                                    *exclusive discounts applied during sign up will not display here
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                        </Box>

                        <Divider sx={{ marginBottom: 4 }} />

                        {!userDataStore?.subscriptionData?.trialActive && (
                            <>
                                <SettingsCancelPlan
                                    subscriptionExpiryTime={userDataStore?.subscriptionData?.subscriptionEndDate}
                                    cancelled={userDataStore?.subscriptionData?.cancelled}
                                    onOpenCancelPlanModal={() => {
                                        setCancelPlanModalOpen(true);
                                    }}
                                />

                                <Divider sx={{ marginBottom: 4 }} />
                            </>
                        )}

                        <SettingsFAQ />

                        <Divider sx={{ marginTop: 5, marginBottom: 5 }} />

                        <Button
                            className="logout-btn"
                            variant="outlined"
                            color="primary"
                            onClick={handleSignout}
                            sx={{
                                borderRadius: '36px',
                                width: '200px',
                                height: 48,
                                borderColor: '#616063',
                                color: '#616063',
                                textAlign: 'center',
                            }}
                        >
                            Logout
                        </Button>
                    </Box>
                </Box>
            )}

            <BlockingSpinner open={isProcessing} />

            <ChangePlanModal
                open={changePlanModalOpen}
                onClose={() => {
                    setChangePlanModalOpen(false);
                }}
                onConfirm={handleConfirmChangePlan}
                selectedPlan={selectedPlan}
            />

            <ConfirmationDialog
                open={downgradeConfirmOpen}
                handleClose={() => setDowngradeConfirmOpen(false)}
                onConfirm={executeFreeDowngrade}
                title="Thinking about changing your plan?"
                message={
                    downgradeEffectiveDateLabel ? (
                        <>
                            You're currently on the {userDataStore?.subscriptionData?.accountType} plan, and you'll
                            continue to enjoy full access until{' '}
                            <strong style={{ whiteSpace: 'nowrap' }}>{downgradeEffectiveDateLabel}</strong>.
                            <br />
                            <br />
                            If you decide to downgrade, nothing changes right away. Your plan will remain active until
                            the date above, and you won't be charged once the Free plan starts.
                            <br />
                            <br />
                            If you'd prefer to stay on {userDataStore?.subscriptionData?.accountType}, you can keep all
                            your features with no interruption.
                        </>
                    ) : (
                        <>
                            You're currently on the {userDataStore?.subscriptionData?.accountType} plan.
                            <br />
                            <br />
                            If you decide to downgrade, you won't be charged once the Free plan starts.
                            <br />
                            <br />
                            If you'd prefer to stay on {userDataStore?.subscriptionData?.accountType}, you can keep all
                            your features with no interruption.
                        </>
                    )
                }
                confirmText="Confirm downgrade"
                cancelText={`Keep my ${userDataStore?.subscriptionData?.accountType} plan`}
            />

            <ConfirmationDialog
                open={enterpriseModalOpen}
                handleClose={() => setEnterpriseModalOpen(false)}
                onConfirm={() => setEnterpriseModalOpen(false)} // single-button close
                title="Excellent choice!"
                message={
                    <>
                        Let’s explore this further. Please email us at{' '}
                        <a
                            href="mailto:hello@cetravets.com"
                            style={{ fontWeight: 'bold', color: '#ff4081', textDecoration: 'none' }}
                        >
                            hello@cetravets.com
                        </a>{' '}
                        detailing the type of enterprise package you’re interested in. If preferred, we can schedule a
                        meeting to discuss the finer details.
                    </>
                }
                confirmText="Close"
                cancelTextReq={false}
            />

            <CancelPlanModal
                open={cancelPlanModalOpen}
                onClose={() => {
                    setCancelPlanModalOpen(false);
                }}
                onConfirm={handleConfirmCancelPlan}
            />
        </CenteredWrapper>
    );
};

const ManageSubscriptionWrapper = (props) => {
    return (
        <Elements stripe={stripePromise}>
            <ManageSubscription {...props} />
        </Elements>
    );
};

export default ManageSubscriptionWrapper;
